/**
 * Source-pane surface presets (especially for light mode exploration).
 *
 * Combines:
 *   - pane background (code surface)
 *   - gutter background
 *   - focused-block wash + left border (sync highlight)
 *
 * Dark already looks good — presets mostly refine light; dark maps to soft
 * variants of the same idea so the control still works.
 */

export type SourceSurfaceId = "default";

export interface SourceSurfacePreset {
  id: SourceSurfaceId;
  /** Short label (toolbar trigger / menu). */
  label: string;
  /** Friendly name in the dropdown. */
  name: string;
  /** One-line description under the name. */
  description: string;
  /** @deprecated alias of description for older callers */
  title: string;
  /** Mini swatch: pane fill + accent (block) for the menu preview. */
  swatch: { pane: string; accent: string };
  /** CSS custom properties applied on the viewer root. */
  vars: {
    paneBg: string;
    gutterBg: string;
    blockBg: string;
    blockBorder: string;
    blockHover: string;
  };
  /** Dark-mode overrides (optional; falls back to light vars). */
  darkVars?: Partial<SourceSurfacePreset["vars"]>;
}

export const SOURCE_SURFACE_PRESETS = {
  default: {
    id: "default",
    label: "Default",
    name: "Soft gray",
    description: "Calm code panel with a light blue focus wash",
    title: "Soft gray — calm code panel with a light blue focus wash",
    swatch: { pane: "#f0f3f8", accent: "#0f6cbd" },
    vars: {
      paneBg: "var(--lilia-code-bg)",
      gutterBg: "var(--lilia-code-gutter-bg)",
      blockBg: "rgba(15, 108, 189, 0.09)",
      blockBorder: "rgba(15, 108, 189, 0.50)",
      blockHover: "rgba(15, 108, 189, 0.05)",
    },
  },
} as const;


export const DEFAULT_SOURCE_SURFACE: SourceSurfaceId = "default";


/** Resolve CSS vars for the active document theme. */
export function resolveSurfaceVars(isDark: boolean): SourceSurfacePreset["vars"] {
  const p = SOURCE_SURFACE_PRESETS.default;
  if (isDark && p.darkVars) {
    return { ...p.vars, ...p.darkVars };
  }
  return p.vars;
}

/** Inline style object for the viewer root. */
export function surfaceStyleVars(isDark: boolean): Record<string, string> {
  const v = resolveSurfaceVars(isDark);
  return {
    "--src-pane-bg": v.paneBg,
    "--src-gutter-bg": v.gutterBg,
    "--src-block-bg": v.blockBg,
    "--src-block-border": v.blockBorder,
    "--src-block-hover": v.blockHover,
  };
}
